import React, { useState, useRef, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import ProgressiveImage from './ProgressiveImage';
import { indexBeforeAfter } from '../utils/contentProvider';

function BeforeAfter({ id }) {
  const [scrollerActive, setScrollerActive] = useState(false);
  const [interacted, setInteracted] = useState(false);

  const mainRef = useRef();
  const sliderBarRef = useRef();
  const afterRef = useRef();

  const scrollerMouseDownHandler = useCallback(() => {
    setScrollerActive(true);
    setInteracted(true);
  }, []);

  const mouseUpHandler = useCallback(() => {
    setScrollerActive(false);
  }, []);

  const scrollIt = useCallback((x) => {
    const transform = Math.max(0, Math.min(x, mainRef.current.offsetWidth));

    afterRef.current.style.width = `${transform}px`;
    sliderBarRef.current.style.left = `${transform - 20}px`;
  }, []);

  const mouseMoveHandler = useCallback(
    (e) => {
      if (scrollerActive) {
        let x = e.type === 'touchmove' ? e.touches[0].pageX : e.pageX;

        x -= mainRef.current.getBoundingClientRect().left;

        scrollIt(x);
      }
    },
    [scrollerActive, scrollIt],
  );

  const scrollHandler = useCallback(() => {
    if (interacted === false && mainRef.current) {
      const { offsetTop, offsetHeight, offsetWidth } = mainRef.current;
      const zero = offsetTop + window.innerHeight * 0.5;
      const hundred = zero + offsetHeight;
      const currentCent = window.pageYOffset + window.innerHeight;
      const percent = (currentCent - zero) / (hundred - zero);

      if (percent < 0) {
        sliderBarRef.current.style.left = `-20px`;
        afterRef.current.style.width = `0px`;
      } else if (percent > 1) {
        sliderBarRef.current.style.left = `${offsetWidth - 20}px`;
        afterRef.current.style.width = `${offsetWidth}px`;
      } else if (percent >= 0 && percent <= 1) {
        sliderBarRef.current.style.left = `${percent * offsetWidth - 20}px`;
        afterRef.current.style.width = `${percent * offsetWidth}px`;
      }
    }
  }, [interacted]);

  useEffect(() => {
    scrollIt(0);
  }, [scrollIt]);

  useEffect(() => {
    window.addEventListener('mouseup', mouseUpHandler);
    window.addEventListener('mouseleave', mouseUpHandler);
    window.addEventListener('touchend', mouseUpHandler);
    window.addEventListener('touchcancel', mouseUpHandler);
    window.addEventListener('mousemove', mouseMoveHandler);
    window.addEventListener('touchmove', mouseMoveHandler);
    window.addEventListener('scroll', scrollHandler);

    return () => {
      window.removeEventListener('mouseup', mouseUpHandler);
      window.removeEventListener('mouseleave', mouseUpHandler);
      window.removeEventListener('touchend', mouseUpHandler);
      window.removeEventListener('touchcancel', mouseUpHandler);
      window.removeEventListener('mousemove', mouseMoveHandler);
      window.removeEventListener('touchmove', mouseMoveHandler);
      window.removeEventListener('scroll', scrollHandler);
    };
  }, [mouseUpHandler, mouseMoveHandler, scrollHandler]);

  return (
    <div className="before-after-main isview" ref={mainRef}>
      <div className="ba-single-slider before">
        <div className="bs-inner">
          <ProgressiveImage
            className="hidden-mobile"
            lowQualitySrc={indexBeforeAfter[id].before.image.small}
            highQualitySrc={indexBeforeAfter[id].before.image.desktop}
          />

          <img
            src={indexBeforeAfter[id].before.image.mobile}
            className="visible-mobile"
            alt="Without AlgoBulls Working Architecture(Bad)"
          />
          <div className="ba-text-points">
            {indexBeforeAfter[id].before.points.map(
              ({ title, description }) => (
                <div key={title} className="ba-point">
                  <h3>{title}</h3>
                  <p>{description}</p>
                </div>
              ),
            )}
          </div>
        </div>
      </div>
      <div ref={afterRef} className="ba-single-slider after">
        <div className="bs-inner">
          <ProgressiveImage
            className="hidden-mobile"
            lowQualitySrc={indexBeforeAfter[id].after.image.small}
            highQualitySrc={indexBeforeAfter[id].after.image.desktop}
          />
          <img
            src={indexBeforeAfter[id].after.image.mobile}
            className="visible-mobile"
            alt="AlgoBulls Working Architecture(Good)"
          />
          <div className="ba-text-points">
            {indexBeforeAfter[id].after.points.map(({ title, description }) => (
              <div key={title} className="ba-point">
                <h3>{title}</h3>
                <p>{description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div
        ref={sliderBarRef}
        className={`slider-bar scroller${scrollerActive ? ' scrolling' : ''}`}
        role="button"
        tabIndex={0}
        onMouseDown={scrollerMouseDownHandler}
        onTouchStart={scrollerMouseDownHandler}
        style={{ left: '100%' }}
        aria-label="slider-scroller">
        <span>
          <svg
            x="0px"
            y="0px"
            viewBox="0 0 19 13"
            style={{ enableBackground: 'new 0 0 19 13' }}>
            <path
              d="M18.4,5.3l-5.9-4.9C12.2,0.1,11.9,0,11.7,0C11.3,0,11,0.4,11,1.2v10.7c0,0.7,0.3,1.2,0.7,1.2
        c0.2,0,0.5-0.1,0.8-0.4l5.9-4.9C18.8,7.4,19,7,19,6.5C19,6,18.8,5.6,18.4,5.3z"
            />
            <path
              d="M0.6,7.7l5.9,4.9C6.8,12.9,7.1,13,7.3,13C7.7,13,8,12.6,8,11.8V1.2C8,0.4,7.7,0,7.3,0C7.1,0,6.8,0.1,6.5,0.4
        L0.6,5.3C0.2,5.6,0,6,0,6.5C0,7,0.2,7.4,0.6,7.7z"
            />
          </svg>
        </span>
      </div>
    </div>
  );
}

BeforeAfter.propTypes = {
  id: PropTypes.number.isRequired,
};

export default BeforeAfter;
